import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './lib/auth';
import { Toaster } from './components/ui/sonner';

import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import AuthCallback from './pages/AuthCallback';
import MarketplacePage from './pages/MarketplacePage';
import LeadDetailPage from './pages/LeadDetailPage';
import BuyerDashboard from './pages/BuyerDashboard';
import SellerDashboard from './pages/SellerDashboard';
import AdminDashboard from './pages/AdminDashboard';
import ProfilePage from './pages/ProfilePage';
import TransactionsPage from './pages/TransactionsPage';
import DealRoomPage from './pages/DealRoomPage';
import SellerSourcesPage from './pages/SellerSourcesPage';
import PayoutsPage from './pages/PayoutsPage';
import PricingPage from './pages/PricingPage';
import LegalPage from './pages/LegalPage';
import StatusPage from './pages/StatusPage';
import VerificationDashboard from './pages/VerificationDashboard';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import PublicPassportPage from './pages/PublicPassportPage';
import MembershipPage from './pages/MembershipPage';
import MembershipSuccessPage from './pages/MembershipSuccessPage';
import PurchaseSuccessPage from './pages/PurchaseSuccessPage';
import DemandProfileCreatePage from './pages/DemandProfileCreatePage';
import DemandProfileWizardPage from './pages/DemandProfileWizardPage';
import OutcomesPage from './pages/OutcomesPage';
import SettingsPage from './pages/SettingsPage';
import AdminUserProfilePage from './pages/AdminUserProfilePage';
import AdminFrameworkPage from './pages/AdminFrameworkPage';

import './App.css';

const ProtectedRoute = ({ children, role }) => {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-gray-500 font-mono text-sm">Authenticating...</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (role && user.role !== role) return <Navigate to="/marketplace" replace />;
  return children;
};

function AppRouter() {
  const location = useLocation();

  // Detect OAuth callback synchronously during render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/marketplace" element={<MarketplacePage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/status" element={<StatusPage />} />
      <Route path="/legal/terms" element={<LegalPage variant="terms" />} />
      <Route path="/legal/privacy" element={<LegalPage variant="privacy" />} />
      <Route path="/legal/refund" element={<LegalPage variant="refund" />} />
      <Route path="/legal/non-circumvention" element={<LegalPage variant="noncircumvention" />} />
      <Route path="/leads/:leadId" element={<LeadDetailPage />} />
      <Route path="/buyer" element={<ProtectedRoute role="buyer"><BuyerDashboard /></ProtectedRoute>} />
      <Route path="/seller" element={<ProtectedRoute role="seller"><SellerDashboard /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute role="admin"><AdminDashboard /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
      <Route path="/transactions" element={<ProtectedRoute><TransactionsPage /></ProtectedRoute>} />
      <Route path="/rooms/:roomId" element={<ProtectedRoute><DealRoomPage /></ProtectedRoute>} />
      <Route path="/seller/sources" element={<ProtectedRoute role="seller"><SellerSourcesPage /></ProtectedRoute>} />
      <Route path="/payouts" element={<ProtectedRoute><PayoutsPage /></ProtectedRoute>} />
      <Route path="/verification" element={<ProtectedRoute><VerificationDashboard /></ProtectedRoute>} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
      <Route path="/verify-email/:token" element={<VerifyEmailPage />} />
      <Route path="/l/:digitalTitle" element={<PublicPassportPage />} />
      <Route path="/membership" element={<ProtectedRoute role="buyer"><MembershipPage /></ProtectedRoute>} />
      <Route path="/membership/success" element={<ProtectedRoute role="buyer"><MembershipSuccessPage /></ProtectedRoute>} />
      <Route path="/buyer/purchases/success" element={<ProtectedRoute role="buyer"><PurchaseSuccessPage /></ProtectedRoute>} />
      <Route path="/buyer/dashboard" element={<ProtectedRoute role="buyer"><BuyerDashboard /></ProtectedRoute>} />
      <Route path="/demand/new" element={<ProtectedRoute role="buyer"><DemandProfileCreatePage /></ProtectedRoute>} />
      <Route path="/demand/wizard" element={<ProtectedRoute role="buyer"><DemandProfileWizardPage /></ProtectedRoute>} />
      <Route path="/outcomes" element={<ProtectedRoute role="buyer"><OutcomesPage /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
      <Route path="/admin/users/:userId" element={<ProtectedRoute role="admin"><AdminUserProfilePage /></ProtectedRoute>} />
      <Route path="/admin/framework" element={<ProtectedRoute role="admin"><AdminFrameworkPage /></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRouter />
        <Toaster position="bottom-right" />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
